import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { Brief } from './types';
import { fetchBriefs, deleteBrief } from './briefsApi';

interface BriefsLandingPageProps {
  onClose: () => void;
  onCreateNew: () => void;
  onEditBrief: (briefId: string) => void;
}

const statusLabels: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'warning' }> = {
  draft: { label: 'Utkast', color: 'default' },
  generated: { label: 'Genererad', color: 'primary' },
  submitted: { label: 'Skickad', color: 'warning' },
  approved: { label: 'Godkänd', color: 'success' },
};

const BriefsLandingPage: React.FC<BriefsLandingPageProps> = ({
  onClose,
  onCreateNew,
  onEditBrief,
}) => {
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadBriefs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBriefs({ search: search || undefined });
      setBriefs(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte hämta briefs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBriefs();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadBriefs();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async (brief: Brief) => {
    if (!window.confirm(`Vill du verkligen ta bort "${brief.title}"?`)) return;
    
    try {
      setDeleting(brief._id);
      await deleteBrief(brief._id);
      setBriefs(prev => prev.filter(b => b._id !== brief._id));
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Kunde inte ta bort brief');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DescriptionIcon sx={{ fontSize: 32, color: '#1B3967' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1B3967' }}>
              Mina Briefer
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateNew}
              sx={{ 
                bgcolor: '#1B3967', 
                '&:hover': { bgcolor: '#0f2341' }
              }}
            >
              Skapa ny brief
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Sökfält */}
        <TextField
          size="small"
          placeholder="Sök briefs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : briefs.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <DescriptionIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Inga briefs ännu
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                Klicka på "Skapa ny brief" för att komma igång
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateNew}
                sx={{ bgcolor: '#1B3967' }}
              >
                Skapa ny brief
              </Button>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Titel</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Produkter</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Uppdaterad</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Åtgärder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {briefs.map((brief) => (
                  <TableRow 
                    key={brief._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onEditBrief(brief._id)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {brief.title}
                      </Typography>
                      {brief.placementSnapshot?.displayName && (
                        <Typography variant="caption" color="textSecondary">
                          {brief.placementSnapshot.displayName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={brief.type === 'print' ? 'Print' : 'Digital'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[brief.status]?.label || brief.status}
                        size="small"
                        color={statusLabels[brief.status]?.color || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {brief.products.length > 0 ? (
                        <Typography variant="body2">
                          {brief.products.length} st
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(brief.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditBrief(brief._id);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(brief);
                        }}
                        disabled={deleting === brief._id}
                      >
                        {deleting === brief._id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default BriefsLandingPage;
